import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Clock, AlertCircle, Search, Download } from "lucide-react";
import AdminShell, { Panel, StatusPill, MockBadge, Btn } from "@/components/admin/AdminShell";
import KpiCard from "@/components/admin/KpiCard";
import { PrimaryTabs, FilterChips } from "@/components/admin/SubTabs";
import DataTable from "@/components/admin/DataTable";
import AdminPager from "@/components/admin/AdminPager";
import { api } from "@/lib/api";
import { asAdminList, useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const TABS = [
  { id: "deposits", label: "INR Deposits", icon: ArrowDown },
  { id: "withdrawals", label: "INR Withdrawals", icon: ArrowUp },
  { id: "all", label: "All Transactions", icon: Clock },
];

const STATUS_FILTERS = [{ id: "", label: "All" }, { id: "pending", label: "Pending" }, { id: "completed", label: "Completed" }, { id: "failed", label: "Failed" }, { id: "on hold", label: "On Hold" }];
const METHOD_FILTERS = [{ id: "", label: "All Methods" }, { id: "UPI", label: "UPI" }, { id: "IMPS", label: "IMPS" }, { id: "NEFT", label: "NEFT" }, { id: "RTGS", label: "RTGS" }];

const fmt = (v) => v?.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }) || "—";

export default function INRPayments() {
  const [tab, setTab] = useState("deposits");
  const [list, setList] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [q, setQ] = useState("");
  const pager = useAdminPager();

  async function load() {
    const params = { skip: pager.skip, limit: pager.limit };
    if (tab !== "all") params.kind = tab.replace(/s$/, "");
    if (statusFilter) params.status = statusFilter;
    const { data } = await api.get("/api/admin/inr/transactions", { params });
    const { items, total } = unwrapAdminList(data, "transactions");
    setList(asAdminList(items));
    pager.setTotal(total);
  }
  useEffect(() => { pager.resetPage(); /* eslint-disable-next-line */ }, [tab, statusFilter]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, statusFilter, pager.page]);

  const rows = asAdminList(list);
  const filtered = rows.filter((t) =>
    (!methodFilter || t.method === methodFilter) &&
    (!q || `${t.tx_id} ${t.user_email} ${t.utr_ref || ""}`.toLowerCase().includes(q.toLowerCase()))
  );

  const todayDep = rows.filter(t => t.kind === "deposit" && t.status === "completed").reduce((s, t) => s + t.amount_inr, 0);
  const todayWith = rows.filter(t => t.kind === "withdrawal" && t.status === "completed").reduce((s, t) => s + t.amount_inr, 0);
  const pendingDep = rows.filter(t => t.kind === "deposit" && t.status === "pending").length;
  const pendingWith = rows.filter(t => t.kind === "withdrawal" && t.status === "pending").length;

  return (
    <AdminShell title="INR Payments">
      <div className="mb-5"><MockBadge phase="D" /></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Today's Deposits" value={fmt(todayDep)} delta="+12%" deltaLabel="vs yesterday" icon={ArrowDown} variant="green" />
        <KpiCard label="Today's Withdrawals" value={fmt(todayWith)} delta="+5%" icon={ArrowUp} variant="blue" />
        <KpiCard label="Pending Deposits" value={pendingDep} icon={Clock} variant="yellow" />
        <KpiCard label="Pending Withdrawals" value={pendingWith} delta="+2" deltaLabel="last hour" icon={AlertCircle} variant="red" />
      </div>

      <PrimaryTabs tabs={TABS} value={tab} onChange={setTab} testIdPrefix="inr-tab" />

      <Panel className="mt-5">
        <div className="p-4 border-b border-[#1E2845] flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-[#0A0F1F] border border-[#1E2845] rounded-[10px] px-3 h-[40px] flex-1 min-w-[240px]">
            <Search size={14} className="text-[#5A6582]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search TX ID, email, UTR..." className="bg-transparent outline-none text-[13px] flex-1" data-testid="inr-search" />
          </div>
          <FilterChips options={STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter} testIdPrefix="inr-status" />
          <FilterChips options={METHOD_FILTERS} value={methodFilter} onChange={setMethodFilter} testIdPrefix="inr-method" />
          <Btn kind="primary"><Download size={13} /> Export</Btn>
        </div>
        <DataTable
          testid="inr-table"
          columns={[
            { key: "tx_id", header: "TX ID", render: (t) => <span className="font-mono text-[11px]">{t.tx_id}</span> },
            { key: "order_ref", header: "Order Ref", render: (t) => <span className="text-[11px] text-[#8892A8]">{t.order_ref || "—"}</span> },
            { key: "user", header: "User", render: (t) => <div><div className="text-[12px]">{t.user_email}</div><div className="text-[10px] text-[#8892A8]">{t.user_phone || t.user_id}</div></div> },
            { key: "method", header: "Method", render: (t) => <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#3B82F622] text-[#3B82F6]">{t.method}</span> },
            { key: "amount", header: "Amount", align: "right", render: (t) => <span className="font-bold tabular-nums">{fmt(t.amount_inr)}</span> },
            { key: "utr", header: "UTR / Ref", render: (t) => <span className="font-mono text-[11px] text-[#8892A8]">{t.utr_ref || "—"}</span> },
            { key: "status", header: "Status", render: (t) => <StatusPill value={t.status} /> },
            { key: "kind", header: "Type" },
            { key: "at", header: "When", render: (t) => <span className="text-[#8892A8] text-[11px]">{new Date(t.created_at).toLocaleString()}</span> },
          ]}
          rows={filtered}
          empty="No transactions match filters."
        />
        <AdminPager
          page={pager.page}
          total={pager.total}
          pageSize={pager.pageSize}
          onPage={pager.setPage}
          testId="inr-pager"
        />
      </Panel>
    </AdminShell>
  );
}
